
import { useState } from 'react';
import { aiTools, toolCategories } from '../../../../mocks/aiDramaTools';
import type { AiTool } from '../../../../mocks/aiDramaTools';

function ToolCard({ tool }: { tool: AiTool }) {
  const [userRating, setUserRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [showDetails, setShowDetails] = useState(false);

  const renderStars = (rating: number, size = 'text-base') => {
    return Array.from({ length: 5 }, (_, i) => {
      const filled = i < Math.floor(rating);
      const half = !filled && i < rating;
      return (
        <i
          key={i}
          className={`${filled ? 'ri-star-fill text-amber-400' : half ? 'ri-star-half-fill text-amber-400' : 'ri-star-line text-gray-300'} ${size}`}
        ></i>
      );
    });
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 hover:shadow-lg hover:border-orange-200 transition-all duration-300 overflow-hidden">
      <div className="p-5">
        <div className="flex items-start gap-4">
          <img
            src={tool.icon}
            alt={tool.name}
            className="w-14 h-14 rounded-xl object-cover flex-shrink-0 border border-gray-100"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-semibold text-gray-900 text-[15px] truncate">{tool.name}</h3>
              <span className="text-xs px-2 py-0.5 bg-orange-50 text-orange-600 rounded-full whitespace-nowrap ml-2">
                {tool.category}
              </span>
            </div>
            <div className="flex items-center gap-2 mb-2">
              <div className="flex items-center gap-0.5">{renderStars(tool.rating, 'text-sm')}</div>
              <span className="text-sm font-semibold text-gray-900">{tool.rating}</span>
              <span className="text-xs text-gray-400">({tool.reviewCount.toLocaleString()})</span>
            </div>
            <p className="text-sm text-gray-600 line-clamp-2 leading-relaxed">{tool.description}</p>
          </div>
        </div>

        <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
          <div className="flex items-center gap-3">
            <span className="text-xs font-medium text-gray-500">
              <i className="ri-price-tag-3-line mr-1"></i>{tool.pricing}
            </span>
            <span className="text-xs text-gray-400">•</span>
            <span className="text-xs text-emerald-600 font-medium">{tool.bestFor}</span>
          </div>
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="text-xs text-orange-500 hover:text-orange-600 font-medium cursor-pointer whitespace-nowrap"
          >
            {showDetails ? 'Less' : 'Details'} <i className={`ri-arrow-${showDetails ? 'up' : 'down'}-s-line`}></i>
          </button>
        </div>
      </div>

      {showDetails && (
        <div className="px-5 pb-5 space-y-3 border-t border-gray-50 bg-gray-50/50">
          <div className="pt-3">
            <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Key Features</h4>
            <div className="flex flex-wrap gap-1.5">
              {tool.features.map((f, i) => (
                <span key={i} className="text-xs px-2.5 py-1 bg-white border border-gray-200 rounded-md text-gray-600">
                  {f}
                </span>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Rate This Tool</h4>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  onClick={() => setUserRating(star)}
                  className="cursor-pointer p-0.5"
                >
                  <i className={`ri-star-${(hoverRating || userRating) >= star ? 'fill text-amber-400' : 'line text-gray-300'} text-xl`}></i>
                </button>
              ))}
              {userRating > 0 && (
                <span className="text-xs text-gray-500 ml-2">Your rating: {userRating}/5</span>
              )}
            </div>
          </div>

          <a
            href={tool.url}
            target="_blank"
            rel="noopener nofollow noreferrer"
            className="flex items-center justify-center w-full py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            Visit {tool.name} <i className="ri-external-link-line ml-1.5"></i>
          </a>
        </div>
      )}
    </div>
  );
}

export default function ToolRatingsSection() {
  const [activeCategory, setActiveCategory] = useState('all');

  const filteredTools = activeCategory === 'all'
    ? aiTools
    : aiTools.filter((t) => t.category === activeCategory);

  return (
    <section className="mb-8">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <span className="w-7 h-7 flex items-center justify-center bg-orange-100 rounded-lg">
              <i className="ri-tools-line text-orange-500 text-sm"></i>
            </span>
            AI Drama Tools &amp; Ratings
          </h2>
          <p className="text-sm text-gray-500 mt-1">Community-rated tools for AI drama production</p>
        </div>
        <span className="text-xs text-gray-400">{filteredTools.length} tools</span>
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap gap-2 mb-5">
        {toolCategories.map((cat) => (
          <button
            key={cat.id}
            onClick={() => setActiveCategory(cat.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
              activeCategory === cat.id
                ? 'bg-orange-500 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <i className={cat.icon}></i>
            {cat.name}
          </button>
        ))}
      </div>

      {/* Tools Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {filteredTools.map((tool) => (
          <ToolCard key={tool.id} tool={tool} />
        ))}
      </div>
    </section>
  );
}
